import { createRootRouteWithContext, HeadContent, Link, Scripts } from "@tanstack/react-router";
import "@/styles/index.css";
import AppShell from "@/components/AppShell";
import { useColorScheme } from "@/hooks/use-color-scheme";
import { siteTitle, siteUrl } from "@/config/seo";
import QueryErrorBoundary from "@/components/ErrorBoundary/QueryErrorBoundary";
import { UiConfig } from "@/config/ui";
import { LinkBuilder } from "@/services/link-builder";
import { isDefined } from "@/utils/is-defined";
import { EnvConfig } from "@/config/env";
import { useWpAuth } from "@/hooks/use-wp-auth";
import cn from "clsx";
import { responseTimeMiddleware } from "@/middleware/server/response-time";
import mainFont from "@/assets/fonts/Gilroy-Regular.woff2";
import secondaryFont from "@/assets/fonts/Gilroy-Black.woff2";
interface MyRouterContext {
  queryClient: QueryClient;
}
export const Route = createRootRouteWithContext<MyRouterContext>()({
  validateSearch: params => params as Record<string, string>,
  notFoundComponent: NotFound,
  server: {
    middleware: [responseTimeMiddleware]
  },
  loader: async ({
    context
  }) => {
    const setPrefs = () => Promise.resolve();
    await setPrefs();
  },
  head: () => {
    const desc = EnvConfig.siteDesc;
    return {
      meta: [{
        charSet: "utf-8"
      }, {
        name: "viewport",
        content: "minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, viewport-fit=cover, minimal-ui"
      }, {
        title: siteTitle
      }, {
        name: "description",
        content: desc
      }, {
        name: "robots",
        content: "index,follow"
      }, {
        name: "googlebot",
        content: "index,follow"
      }, {
        name: "twitter:site",
        content: "@strustatjana"
      }, {
        name: "twitter:creator",
        content: "@strustatjana"
      }, {
        name: "og:title",
        content: siteTitle
      }, {
        name: "og:description",
        content: desc
      }, {
        name: "og:url",
        content: siteUrl
      }, {
        name: "og:image",
        content: `${siteUrl}icons/icon-512x512.png`
      }, {
        name: "og:image:width",
        content: "512"
      }, {
        name: "og:image:height",
        content: "512"
      }, {
        name: "og:locale",
        content: "ru"
      }, {
        name: "apple-mobile-web-app-capable",
        content: "yes"
      }, {
        name: "apple-mobile-web-app-status-bar-style",
        content: "#5ccaa3"
      }, {
        httpEquiv: "x-ua-compatible",
        content: "IE=edge; chrome=1"
      }],
      links: [{
        rel: "preload",
        href: mainFont,
        as: "font",
        type: "font/woff2"
      }, {
        rel: "preload",
        href: secondaryFont,
        as: "font",
        type: "font/woff2"
      }, {
        rel: "mask-icon",
        href: "/safari-pinned-tab.svg",
        color: "#5bbad5"
      }, {
        rel: "icon",
        type: "image/png",
        sizes: "16x16",
        href: "/favicon-16x16.png"
      }, {
        rel: "icon",
        type: "image/png",
        sizes: "32x32",
        href: "/favicon-32x32.png"
      }, {
        rel: "apple-touch-icon",
        sizes: "180x180",
        href: "/apple-touch-icon.png"
      }, EnvConfig.prod ? {
        rel: "manifest",
        href: "/manifest.webmanifest"
      } : null].filter(isDefined),
      styles: [{
        children: `:root { --primary-color: ${UiConfig.primaryColor}; }`
      }],
      scripts: [EnvConfig.prod ? {
        src: "/register-sw.js"
      } : null].filter(isDefined)
    };
  },
  shellComponent: RootDocument
});
function NotFound() {
  return <div style={{
    padding: "1rem",
    textAlign: "center"
  }}>
      <h1 style={{
      fontWeight: 900,
      fontSize: "1.5rem"
    }}>Не найдено</h1>
      <Link style={{
      color: "var(--primary-color)"
    }} to={LinkBuilder.build({
      route: "home"
    })}>
        Домой
      </Link>
    </div>;
}
function RootDocument({
  children
}: {
  children: ReactNode;
}) {
  const colorScheme = useColorScheme();
  const wpAuth = useWpAuth();
  return <html lang="ru" className={cn(colorScheme, wpAuth && "wp-authorized")}>
      <head>
        <HeadContent />
      </head>
      <body>
        <QueryErrorBoundary>
          <AppShell>{children}</AppShell>
        </QueryErrorBoundary>
        <Scripts />
      </body>
    </html>;
}